import { doc, getDoc, onSnapshot, runTransaction, Unsubscribe } from 'firebase/firestore';
import { db } from '../config/firebase';
import { applyAction, createInitialGame, GameAction, GameError, GameState } from '../game';
import { generateGameId } from '../game/ids';

const COLLECTION = 'games';

function gameRef(gameId: string) {
  return doc(db, COLLECTION, gameId);
}

export async function createGameDocument(hostId: string, hostName: string): Promise<string> {
  for (let attempt = 0; attempt < 6; attempt += 1) {
    const id = generateGameId();
    const ref = gameRef(id);
    try {
      await runTransaction(db, async (tx) => {
        const snap = await tx.get(ref);
        if (snap.exists()) {
          throw new GameError('collision');
        }
        tx.set(ref, createInitialGame({ id, hostId, hostName }));
      });
      return id;
    } catch (error) {
      const message = error instanceof Error ? error.message : '';
      if (message.includes('collision')) {
        continue;
      }
      throw error;
    }
  }
  throw new GameError('Could not create a game code. Please try again.');
}

export async function dispatchGameAction(gameId: string, action: GameAction): Promise<GameState> {
  const ref = gameRef(gameId);
  return runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) {
      throw new GameError('Game not found');
    }
    const next = applyAction(snap.data() as GameState, action);
    tx.set(ref, next);
    return next;
  });
}

export function subscribeToGame(
  gameId: string,
  onNext: (game: GameState | null) => void,
  onError?: (error: Error) => void
): Unsubscribe {
  return onSnapshot(
    gameRef(gameId),
    (snap) => {
      onNext(snap.exists() ? (snap.data() as GameState) : null);
    },
    (error) => {
      onError?.(error);
    }
  );
}

export async function fetchGame(gameId: string): Promise<GameState | null> {
  const snap = await getDoc(gameRef(gameId));
  return snap.exists() ? (snap.data() as GameState) : null;
}
